import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Award, BookOpen, Star, FileOutput, Printer, X, ChevronRight } from 'lucide-react';
import { studentsAPI } from '../services/api';
import { useYear } from '../context/YearContext';

// ── Helpers ────────────────────────────────────────────────────────────────────
function today() {
  return new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
}

// ── Certificate field definitions ─────────────────────────────────────────────
const CERTIFICATE_TYPES = [
  {
    id: 'bonafide',
    label: 'Bonafide Certificate',
    shortLabel: 'Bonafide',
    icon: Award,
    color: '#1a56db',
    bgColor: '#eff6ff',
    borderColor: '#93c5fd',
    fields: [
      { key: 'student_name',  label: 'Student Name',           placeholder: 'e.g. Ravi Kumar' },
      { key: 'father_name',   label: "Father's Name",          placeholder: 'e.g. Suresh Kumar' },
      { key: 'mother_name',   label: "Mother's Name",          placeholder: 'e.g. Lakshmi Devi' },
      { key: 'class',         label: 'Class / Section',        placeholder: 'e.g. VIII - A' },
      { key: 'academic_year', label: 'Academic Year',          placeholder: 'e.g. 2024-25' },
      { key: 'admission_no',  label: 'Admission Number',       placeholder: 'e.g. 1042' },
      { key: 'dob',           label: 'Date of Birth',          placeholder: 'e.g. 15-03-2012' },
      { key: 'caste',         label: 'Caste',                  placeholder: 'e.g. BC-B' },
      { key: 'religion',      label: 'Religion',               placeholder: 'e.g. Hindu' },
      { key: 'nationality',   label: 'Nationality',            placeholder: 'Indian' },
      { key: 'medium',        label: 'Medium of Instruction',  placeholder: 'e.g. Telugu Medium' },
      { key: 'purpose',       label: 'Purpose of Certificate', placeholder: 'e.g. Bank Account Opening' },
      { key: 'serial_no',     label: 'Certificate Serial No',  placeholder: 'e.g. BON/2024/001' },
      { key: 'issue_date',    label: 'Date of Issue',          placeholder: today() },
    ],
  },
  {
    id: 'study',
    label: 'Study Certificate',
    shortLabel: 'Study',
    icon: BookOpen,
    color: '#047857',
    bgColor: '#ecfdf5',
    borderColor: '#6ee7b7',
    fields: [
      { key: 'student_name',  label: 'Student Name',           placeholder: 'e.g. Anil Reddy' },
      { key: 'father_name',   label: "Father's Name",          placeholder: 'e.g. Venkat Reddy' },
      { key: 'mother_name',   label: "Mother's Name",          placeholder: 'e.g. Vijaya Reddy' },
      { key: 'class',         label: 'Class / Section',        placeholder: 'e.g. VI - C' },
      { key: 'academic_year', label: 'Academic Year',          placeholder: 'e.g. 2024-25' },
      { key: 'admission_no',  label: 'Admission Number',       placeholder: 'e.g. 1190' },
      { key: 'dob',           label: 'Date of Birth',          placeholder: 'e.g. 08-01-2013' },
      { key: 'caste',         label: 'Caste',                  placeholder: 'e.g. OC' },
      { key: 'religion',      label: 'Religion',               placeholder: 'e.g. Hindu' },
      { key: 'nationality',   label: 'Nationality',            placeholder: 'Indian' },
      { key: 'medium',        label: 'Medium of Instruction',  placeholder: 'e.g. Telugu Medium' },
      { key: 'purpose',       label: 'Purpose',                placeholder: 'e.g. Scholarship Application' },
      { key: 'serial_no',     label: 'Certificate Serial No',  placeholder: 'e.g. STD/2024/012' },
      { key: 'issue_date',    label: 'Date of Issue',          placeholder: today() },
    ],
  },
  {
    id: 'conduct',
    label: 'Conduct Certificate',
    shortLabel: 'Conduct',
    icon: Star,
    color: '#b45309',
    bgColor: '#fffbeb',
    borderColor: '#fcd34d',
    fields: [
      { key: 'student_name',  label: 'Student Name',                placeholder: 'e.g. Kavya Nair' },
      { key: 'father_name',   label: "Father's Name",               placeholder: 'e.g. Sunil Nair' },
      { key: 'mother_name',   label: "Mother's Name",               placeholder: 'e.g. Rekha Nair' },
      { key: 'class',         label: 'Class / Section',             placeholder: 'e.g. IX - A' },
      { key: 'academic_year', label: 'Academic Year',               placeholder: 'e.g. 2024-25' },
      { key: 'admission_no',  label: 'Admission Number',            placeholder: 'e.g. 1305' },
      { key: 'period_from',   label: 'Studied From (Year)',         placeholder: 'e.g. 2020' },
      { key: 'period_to',     label: 'Studied To (Year)',           placeholder: 'e.g. 2024' },
      { key: 'conduct',       label: 'Conduct',                     placeholder: 'e.g. Excellent' },
      { key: 'character',     label: 'Character',                   placeholder: 'e.g. Good' },
      { key: 'activities',    label: 'Extra-curricular Activities',  placeholder: 'e.g. School Pupil Leader (SPL)' },
      { key: 'remarks',       label: 'Additional Remarks',          placeholder: 'e.g. Sincere and hardworking' },
      { key: 'serial_no',     label: 'Certificate Serial No',       placeholder: 'e.g. CON/2024/007' },
      { key: 'issue_date',    label: 'Date of Issue',               placeholder: today() },
    ],
  },
  {
    id: 'transfer',
    label: 'Transfer Certificate',
    shortLabel: 'TC',
    icon: FileOutput,
    color: '#7c3aed',
    bgColor: '#f5f3ff',
    borderColor: '#c4b5fd',
    fields: [
      { key: 'student_name',      label: 'Student Name',                         placeholder: 'e.g. Ravi Kumar' },
      { key: 'father_name',       label: "Father's / Guardian's Name",           placeholder: 'e.g. Suresh Kumar' },
      { key: 'mother_name',       label: "Mother's Name",                        placeholder: 'e.g. Lakshmi Devi' },
      { key: 'nationality',       label: 'Nationality',                          placeholder: 'Indian' },
      { key: 'religion',          label: 'Religion',                             placeholder: 'e.g. Hindu' },
      { key: 'caste',             label: 'Community / Caste',                    placeholder: 'e.g. BC-B' },
      { key: 'dob',               label: 'Date of Birth',                        placeholder: 'e.g. 15-03-2012' },
      { key: 'admission_no',      label: 'Admission Number',                     placeholder: 'e.g. 1042' },
      { key: 'date_of_admission', label: 'Date of First Admission in School',    placeholder: 'e.g. 01-06-2018' },
      { key: 'class',             label: 'Class at Time of Leaving',             placeholder: 'e.g. VIII - A' },
      { key: 'fee_paid',          label: 'School Fees Paid Up To',               placeholder: 'e.g. March 2026' },
      { key: 'promotion',         label: 'Qualified for Promotion',              placeholder: 'e.g. Yes / No / N/A' },
      { key: 'conduct',           label: 'Conduct and Character',                placeholder: 'e.g. Good' },
      { key: 'date_of_leaving',   label: 'Date of Leaving',                      placeholder: today() },
      { key: 'reason_leaving',    label: 'Reason for Leaving',                   placeholder: "e.g. Parent's Request" },
      { key: 'remarks',           label: 'Remarks',                              placeholder: 'NIL' },
      { key: 'serial_no',         label: 'TC Serial No',                         placeholder: 'e.g. TC/2024/001' },
      { key: 'issue_date',        label: 'Date of Issue',                        placeholder: today() },
    ],
  },
];

// ── Print stylesheet (injected into popup window) ──────────────────────────────
const PRINT_STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Times New Roman', Times, serif; background: #fff; color: #111; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  @page { size: A4; margin: 12mm 16mm; }

  .cert-print-page {
    width: 100%;
    min-height: 257mm;
    padding: 0;
    position: relative;
    border: 3px double #8B0000;
    padding: 12mm 16mm;
  }

  /* ── Header ── */
  .cert-header-block { text-align: center; margin-bottom: 10px; }
  .cert-header-inner { display: flex; align-items: center; justify-content: center; gap: 14px; margin-bottom: 6px; }
  .cert-school-logo { width: 68px; height: 68px; object-fit: contain; }
  .cert-school-info { text-align: center; }
  .cert-school-name { font-size: 22px; font-weight: 700; color: #8B0000; letter-spacing: .8px; text-transform: uppercase; font-family: 'Times New Roman', serif; line-height: 1.2; }
  .cert-school-affil { font-size: 11.5px; color: #444; margin-top: 3px; font-style: italic; }
  .cert-school-affil2 { font-size: 10.5px; color: #555; margin-top: 2px; }
  .cert-divider-top { border: none; border-top: 2.5px solid #8B0000; margin: 8px 0 3px; }
  .cert-divider-thin { border: none; border-top: 1px solid #8B0000; margin: 2px 0 6px; }
  .cert-subtitle-bar { background: #8B0000; color: #fff; padding: 5px 0; font-size: 14px; font-weight: 700; letter-spacing: 3px; text-align: center; margin: 6px 0 4px; text-transform: uppercase; }

  /* ── Body ── */
  .cert-body { padding: 4px 0; }
  .cert-meta-row { display: flex; justify-content: space-between; font-size: 12px; margin-bottom: 12px; color: #333; }
  .cert-para { font-size: 13.5px; line-height: 2.1; margin-bottom: 12px; text-align: justify; }
  .cert-para strong { border-bottom: 1px solid #333; padding-bottom: 1px; }
  .cert-underline { border-bottom: 1px solid #333; display: inline-block; min-width: 180px; }

  /* ── TC table ── */
  .cert-tc-table { width: 100%; border-collapse: collapse; font-size: 13px; margin: 8px 0; }
  .cert-tc-table td { padding: 4px 6px; vertical-align: top; line-height: 1.9; }
  .cert-tc-table td:first-child { width: 28px; color: #555; font-weight: 600; white-space: nowrap; }
  .cert-tc-table td:nth-child(2) { width: 240px; }
  .cert-tc-table td:nth-child(3) { width: 14px; }
  .cert-tc-table td:nth-child(4) strong { border-bottom: 1px solid #333; padding-bottom: 1px; }
  .cert-tc-divider td { padding: 0; }
  .cert-tc-divider td div { border-top: 1px dashed #ccc; margin: 3px 0; }

  /* ── Signature row ── */
  .cert-signature-row { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 48px; }
  .cert-sig-block { text-align: left; font-size: 12px; color: #222; }
  .cert-sig-line { border-top: 1px solid #333; width: 150px; margin: 0 0 5px 0; }
  .cert-sig-right { text-align: right; }
  .cert-sig-right .cert-sig-line { margin: 0 0 5px auto; }
  .cert-issue-date { font-size: 12px; margin-bottom: 4px; }
  .cert-seal-text { font-size: 10px; color: #888; font-style: italic; }

  /* ── Footer note ── */
  .cert-footer-note { margin-top: 18px; border-top: 1px solid #ccc; padding-top: 8px; font-size: 10.5px; color: #666; font-style: italic; text-align: center; }
`;

// ── Shared header sub-component ────────────────────────────────────────────────
function CertHeader({ schoolName, schoolLogoUrl, subtitle }) {
  return (
    <div className="cert-header-block">
      <div className="cert-header-inner">
        {schoolLogoUrl && (
          <img src={schoolLogoUrl} alt="School Logo" className="cert-school-logo" />
        )}
        <div className="cert-school-info">
          <h1 className="cert-school-name">{schoolName || 'School Name'}</h1>
          <p className="cert-school-affil">Recognised by the Government of Telangana (TG)</p>
          <p className="cert-school-affil2">Affiliated to Board of Secondary Education, Telangana — BSE Telangana</p>
        </div>
      </div>
      <hr className="cert-divider-top" />
      <hr className="cert-divider-thin" />
      <div className="cert-subtitle-bar">{subtitle}</div>
    </div>
  );
}

// ── Signature: only Headmaster (Bonafide / Study / Conduct) ───────────────────
function CertSignatureRowHM({ date }) {
  return (
    <div className="cert-signature-row">
      <div className="cert-sig-block">
        <p className="cert-issue-date">Date: <strong>{date || today()}</strong></p>
        <p className="cert-seal-text">(Office Seal)</p>
      </div>
      <div className="cert-sig-block cert-sig-right">
        <div className="cert-sig-line" />
        <p>Headmaster / Principal</p>
      </div>
    </div>
  );
}

const blank = (w) => <span className="cert-underline" style={{ minWidth: w || 180 }}>&nbsp;</span>;

// ── Bonafide Certificate ───────────────────────────────────────────────────────
function BonafideCertificate({ data, schoolName, schoolLogoUrl }) {
  return (
    <div className="cert-print-page">
      <CertHeader schoolName={schoolName} schoolLogoUrl={schoolLogoUrl} subtitle="BONAFIDE CERTIFICATE" />
      <div className="cert-body">
        <div className="cert-meta-row">
          <span>Sl. No.: <strong>{data.serial_no || blank(120)}</strong></span>
          <span>Date: <strong>{data.issue_date || today()}</strong></span>
        </div>
        <p className="cert-para">
          This is to certify that <strong>{data.student_name || blank()}</strong>,
          {' '}Son / Daughter of <strong>{data.father_name || blank()}</strong>{' '}
          and <strong>{data.mother_name || blank()}</strong>,
          bearing Admission No.&nbsp;<strong>{data.admission_no || blank(80)}</strong>,
          is a bonafide student of this institution and is studying in
          Class&nbsp;<strong>{data.class || blank(80)}</strong>{' '}
          ({data.medium || 'Telugu Medium'}) during the Academic Year&nbsp;
          <strong>{data.academic_year || blank(80)}</strong>.
        </p>
        <p className="cert-para">
          His / Her Date of Birth as per school records is{' '}
          <strong>{data.dob || blank(150)}</strong>.
        </p>
        <p className="cert-para">
          Caste: <strong>{data.caste || blank(100)}</strong>
          &nbsp;&nbsp;&nbsp;
          Religion: <strong>{data.religion || blank(100)}</strong>
          &nbsp;&nbsp;&nbsp;
          Nationality: <strong>{data.nationality || 'Indian'}</strong>
        </p>
        <p className="cert-para">
          This certificate is issued on the request of the student / parent for the purpose of{' '}
          <strong>{data.purpose || blank(200)}</strong>.
        </p>
        <CertSignatureRowHM date={data.issue_date} />
        <p className="cert-footer-note">
          Note: This certificate is valid only when it bears the seal and signature of the Headmaster / Principal.
        </p>
      </div>
    </div>
  );
}

// ── Study Certificate ──────────────────────────────────────────────────────────
function StudyCertificate({ data, schoolName, schoolLogoUrl }) {
  return (
    <div className="cert-print-page">
      <CertHeader schoolName={schoolName} schoolLogoUrl={schoolLogoUrl} subtitle="STUDY CERTIFICATE" />
      <div className="cert-body">
        <div className="cert-meta-row">
          <span>Sl. No.: <strong>{data.serial_no || blank(120)}</strong></span>
          <span>Date: <strong>{data.issue_date || today()}</strong></span>
        </div>
        <p className="cert-para">
          This is to certify that <strong>{data.student_name || blank()}</strong>,
          {' '}Son / Daughter of <strong>{data.father_name || blank()}</strong>{' '}
          and <strong>{data.mother_name || blank()}</strong>,
          bearing Admission No.&nbsp;<strong>{data.admission_no || blank(80)}</strong>,
          is studying in Class&nbsp;<strong>{data.class || blank(80)}</strong>{' '}
          in <strong>{data.medium || 'Telugu Medium'}</strong> in this institution
          during the Academic Year&nbsp;<strong>{data.academic_year || blank(80)}</strong>.
        </p>
        <p className="cert-para">
          His / Her Date of Birth as per school records is{' '}
          <strong>{data.dob || blank(150)}</strong>.
        </p>
        <p className="cert-para">
          Caste: <strong>{data.caste || blank(100)}</strong>
          &nbsp;&nbsp;&nbsp;
          Religion: <strong>{data.religion || blank(100)}</strong>
          &nbsp;&nbsp;&nbsp;
          Nationality: <strong>{data.nationality || 'Indian'}</strong>
        </p>
        <p className="cert-para">
          This certificate is issued for the purpose of{' '}
          <strong>{data.purpose || blank(200)}</strong>.
        </p>
        <CertSignatureRowHM date={data.issue_date} />
        <p className="cert-footer-note">
          Note: This certificate is valid only when it bears the seal and signature of the Headmaster / Principal.
        </p>
      </div>
    </div>
  );
}

// ── Conduct Certificate ────────────────────────────────────────────────────────
function ConductCertificate({ data, schoolName, schoolLogoUrl }) {
  return (
    <div className="cert-print-page">
      <CertHeader schoolName={schoolName} schoolLogoUrl={schoolLogoUrl} subtitle="CONDUCT CERTIFICATE" />
      <div className="cert-body">
        <div className="cert-meta-row">
          <span>Sl. No.: <strong>{data.serial_no || blank(120)}</strong></span>
          <span>Date: <strong>{data.issue_date || today()}</strong></span>
        </div>
        <p className="cert-para">
          This is to certify that <strong>{data.student_name || blank()}</strong>,
          {' '}Son / Daughter of <strong>{data.father_name || blank()}</strong>{' '}
          and <strong>{data.mother_name || blank()}</strong>,
          bearing Admission No.&nbsp;<strong>{data.admission_no || blank(80)}</strong>,
          studied in this institution from{' '}
          <strong>{data.period_from || blank(60)}</strong> to{' '}
          <strong>{data.period_to || blank(60)}</strong>{' '}
          in Class&nbsp;<strong>{data.class || blank(80)}</strong>{' '}
          during the Academic Year&nbsp;<strong>{data.academic_year || blank(80)}</strong>.
        </p>
        <p className="cert-para">
          During his / her stay in this school, he / she has shown{' '}
          <strong>{data.conduct || 'Good'}</strong> conduct and{' '}
          <strong>{data.character || 'Good'}</strong> character.
        </p>
        {data.activities && (
          <p className="cert-para">
            He / She has actively participated in{' '}
            <strong>{data.activities}</strong>.
          </p>
        )}
        {data.remarks && (
          <p className="cert-para">
            <strong>Remarks:</strong> {data.remarks}
          </p>
        )}
        <p className="cert-para">
          This certificate is issued as per the request of the student / parent for future reference and use.
        </p>
        <CertSignatureRowHM date={data.issue_date} />
        <p className="cert-footer-note">
          Note: This certificate is valid only when it bears the seal and signature of the Headmaster / Principal.
        </p>
      </div>
    </div>
  );
}

// ── Transfer Certificate ───────────────────────────────────────────────────────
function TransferCertificate({ data, schoolName, schoolLogoUrl }) {
  const rows = [
    ["1.",  "Name of the Student",                                              data.student_name],
    ["2.",  "Father's / Guardian's Name",                                       data.father_name],
    ["3.",  "Mother's Name",                                                    data.mother_name],
    ["4.",  "Nationality",                                                      data.nationality || 'Indian'],
    ["5.",  "Religion",                                                         data.religion],
    ["6.",  "Community (Caste)",                                                data.caste],
    ["7.",  "Date of Birth (as per school records)",                            data.dob],
    ["8.",  "Admission Number",                                                 data.admission_no],
    ["9.",  "Date of First Admission in School",                                data.date_of_admission],
    ["10.", "Class in which the student was studying at the time of leaving",   data.class],
    ["11.", "School fees paid up to",                                           data.fee_paid || 'Yes'],
    ["12.", "Whether qualified for promotion to the next higher class",         data.promotion],
    ["13.", "Conduct and Character",                                            data.conduct],
    ["14.", "Date on which the student left the school",                        data.date_of_leaving],
    ["15.", "Reason for leaving",                                               data.reason_leaving],
    ["16.", "Remarks",                                                          data.remarks || 'NIL'],
  ];

  return (
    <div className="cert-print-page">
      <CertHeader schoolName={schoolName} schoolLogoUrl={schoolLogoUrl} subtitle="TRANSFER CERTIFICATE" />
      <div className="cert-body">
        <div className="cert-meta-row">
          <span>Sl. No.: <strong>{data.serial_no || blank(120)}</strong></span>
          <span>Date: <strong>{data.issue_date || today()}</strong></span>
        </div>
        <table className="cert-tc-table">
          <tbody>
            {rows.map(([num, label, value], i) => (
              <React.Fragment key={i}>
                <tr>
                  <td>{num}</td>
                  <td>{label}</td>
                  <td>:</td>
                  <td><strong>{value || blank()}</strong></td>
                </tr>
                {i < rows.length - 1 && (
                  <tr className="cert-tc-divider"><td colSpan={4}><div /></td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        <CertSignatureRowHM date={data.issue_date} />
        <p className="cert-footer-note">
          Note: This certificate is valid only when it bears the seal and signature of the Headmaster / Principal.
        </p>
      </div>
    </div>
  );
}

const CERT_COMPONENTS = {
  bonafide: BonafideCertificate,
  study:    StudyCertificate,
  conduct:  ConductCertificate,
  transfer: TransferCertificate,
};

// ── Variables Modal ────────────────────────────────────────────────────────────
function VariablesModal({ certType, schoolName, schoolLogoUrl, onClose }) {
  const { year } = useYear();
  const initDefaults = () => {
    const defaults = {};
    certType.fields.forEach((f) => {
      if (f.key === 'issue_date' || f.key === 'date_of_leaving') defaults[f.key] = today();
      else if (f.key === 'nationality') defaults[f.key] = 'Indian';
      else if (f.key === 'fee_paid') defaults[f.key] = 'Yes';
      else if (f.key === 'remarks' || f.key === 'scholarship') defaults[f.key] = 'NIL';
      else defaults[f.key] = '';
    });
    return defaults;
  };

  const [values, setValues] = useState(initDefaults);
  const [showPreview, setShowPreview] = useState(false);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    if (!year) return;
    studentsAPI.getClasses(year).then((res) => {
      if (res.data?.success) setClasses(res.data.data);
    }).catch(() => {});
  }, [year]);

  const handleClassChange = async (cls) => {
    setSelectedClass(cls);
    setStudents([]);
    setValues((prev) => ({ ...initDefaults(), issue_date: prev.issue_date }));
    if (!cls) return;
    setLoadingStudents(true);
    try {
      const res = await studentsAPI.getAll({ class: cls, year });
      if (res.data?.success) setStudents(res.data.data);
    } catch (_) {}
    setLoadingStudents(false);
  };

  const handleStudentSelect = (studentId) => {
    const student = students.find((s) => String(s.student_id) === studentId);
    if (!student) {
      setValues((prev) => ({ ...initDefaults(), issue_date: prev.issue_date }));
      return;
    }
    const dob = student.dob
      ? new Date(student.dob).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-')
      : '';
    setValues((prev) => ({
      ...prev,
      student_name:  student.student_name  || '',
      father_name:   student.father_name   || '',
      mother_name:   student.mother_name   || '',
      class:         student.class         || '',
      academic_year: student.academic_year || '',
      admission_no:  student.admission_no  || '',
      dob:           dob,
      caste:         student.caste         || '',
    }));
  };

  const handleChange = (key, val) => setValues((prev) => ({ ...prev, [key]: val }));

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const printWindow = window.open('', '_blank', 'width=900,height=720');
    printWindow.document.write(`<!DOCTYPE html><html><head>
      <title>${certType.label} — ${schoolName || 'School'}</title>
      <meta charset="utf-8" />
      <style>${PRINT_STYLES}</style>
    </head><body>${content.innerHTML}</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 600);
  };

  const canPreview = selectedClass && values.student_name;

  const CertPreview = CERT_COMPONENTS[certType.id];

  // Fields excluding class and student_name (handled separately as dropdowns at top)
  const otherFields = certType.fields.filter((f) => f.key !== 'class' && f.key !== 'student_name');

  return createPortal(
    <div className="modal-overlay cert-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box cert-vars-modal">
        <div className="modal-header">
          <h2>
            <certType.icon size={17} style={{ marginRight: 7, verticalAlign: 'text-bottom', color: certType.color }} />
            {certType.label} — Fill Details
          </h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="cert-modal-tabs">
          <button className={`cert-modal-tab${!showPreview ? ' active' : ''}`} onClick={() => setShowPreview(false)}>
            Fill Details
          </button>
          <button className={`cert-modal-tab${showPreview ? ' active' : ''}`} onClick={() => setShowPreview(true)} disabled={!canPreview}>
            Preview Certificate
          </button>
        </div>

        {!showPreview ? (
          <div className="modal-body cert-fields-body">
            <div className="cert-fields-note">
              Select a class and student — all details will be prefilled automatically.
            </div>
            <div className="form-grid">
              {/* Class dropdown — mandatory, first */}
              <div className="form-group" key="class">
                <label>Class / Section <span style={{ color: '#e53e3e' }}>*</span></label>
                <select
                  value={selectedClass}
                  onChange={(e) => handleClassChange(e.target.value)}
                  required
                  style={{ width: '100%' }}
                >
                  <option value="">— Select Class —</option>
                  {classes.map((cls) => (
                    <option key={cls} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>

              {/* Student name dropdown — mandatory, second */}
              <div className="form-group" key="student_name">
                <label>Student Name <span style={{ color: '#e53e3e' }}>*</span></label>
                <select
                  value={students.find((s) => s.student_name === values.student_name)?.student_id || ''}
                  onChange={(e) => handleStudentSelect(e.target.value)}
                  required
                  disabled={!selectedClass || loadingStudents}
                  style={{ width: '100%' }}
                >
                  <option value="">
                    {!selectedClass ? '— Select Class first —' : loadingStudents ? 'Loading…' : '— Select Student —'}
                  </option>
                  {students.map((s) => (
                    <option key={s.student_id} value={s.student_id}>{s.student_name}</option>
                  ))}
                </select>
              </div>

              {/* Remaining fields */}
              {otherFields.map((field) => (
                <div className="form-group" key={field.key}>
                  <label>{field.label}</label>
                  <input
                    type="text"
                    value={values[field.key] || ''}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                    placeholder={field.placeholder}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="modal-body cert-preview-body">
            <div className="cert-preview-wrap" ref={printRef}>
              <CertPreview data={values} schoolName={schoolName} schoolLogoUrl={schoolLogoUrl} />
            </div>
          </div>
        )}

        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          {!showPreview ? (
            <button type="button" className="btn btn-primary" onClick={() => setShowPreview(true)} disabled={!canPreview}>
              Preview <ChevronRight size={15} style={{ marginLeft: 4, verticalAlign: 'middle' }} />
            </button>
          ) : (
            <button type="button" className="btn btn-success cert-print-btn" onClick={handlePrint}>
              <Printer size={15} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Print Certificate
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Certificate card ───────────────────────────────────────────────────────────
const CERT_DESCRIPTIONS = {
  bonafide: 'Certifies the student is currently enrolled. Includes caste, religion & nationality.',
  study:    'Confirms the student is studying in the current year. Includes community details.',
  conduct:  'Certifies conduct, character and participation for the period of study.',
  transfer: 'Official TC for students leaving the school. BSE Telangana numbered-table format.',
};

function CertCard({ cert, onSelect }) {
  const Icon = cert.icon;
  return (
    <button
      className="cert-card"
      style={{ '--cert-color': cert.color, '--cert-bg': cert.bgColor, '--cert-border': cert.borderColor }}
      onClick={() => onSelect(cert)}
      aria-label={`Open ${cert.label}`}
    >
      <div className="cert-card-icon-wrap"><Icon size={30} /></div>
      <div className="cert-card-info">
        <h3 className="cert-card-title">{cert.label}</h3>
        <p className="cert-card-desc">{CERT_DESCRIPTIONS[cert.id]}</p>
      </div>
      <div className="cert-card-arrow"><ChevronRight size={20} /></div>
    </button>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function Certificates({ schoolName, schoolLogoUrl }) {
  const [activeCert, setActiveCert] = useState(null);

  return (
    <div className="page-wrapper">
      <div className="page-header">
        <div>
          <h1 className="page-title">Certificates</h1>
          <p className="page-subtitle">Generate and print official school certificates — BSE Telangana (TG) format</p>
        </div>
      </div>

      <div className="cert-grid">
        {CERTIFICATE_TYPES.map((cert) => (
          <CertCard key={cert.id} cert={cert} onSelect={setActiveCert} />
        ))}
      </div>

      <div className="cert-info-banner">
        <Award size={16} style={{ marginRight: 8, flexShrink: 0, color: '#4f46e5' }} />
        <span>
          Templates follow the <strong>BSE Telangana (TG)</strong> format for recognised private schools.
          Signed by Headmaster / Principal only. School name is auto-filled from your school profile.
        </span>
      </div>

      {activeCert && (
        <VariablesModal
          certType={activeCert}
          schoolName={schoolName}
          schoolLogoUrl={schoolLogoUrl}
          onClose={() => setActiveCert(null)}
        />
      )}
    </div>
  );
}
